import React, { useEffect, useState } from "react";
import axios from "axios";
import "../e_commerce_portal/products/products.scss"; // Style this file like products.scss
import { toast } from "react-toastify";

interface Product {
  _id: string;
  productName: string;
  productImage: string;
  mrpPrice: number;
  selPrice: number;
}

const FestivalSalesProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState<
    Record<string, { mrpPrice: number; selPrice: number }>
  >({});
  const [futureStartDate, setFutureStartDate] = useState("");
  const [futureEndDate, setFutureEndDate] = useState("");

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(
        "https://api.fitoja.in/api/get-all-product"
      );
      setProducts(response.data.data);
    } catch (error) {
      console.error("Error fetching products", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const handleCheckboxChange = (id: string) => {
    setSelectedProducts((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const handleEditChange = (
    id: string,
    field: "mrpPrice" | "selPrice",
    value: string
  ) => {
    const parsedValue = parseFloat(value);
    setEditValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: isNaN(parsedValue) ? 0 : parsedValue,
      },
    }));
  };

  const handleUpdatePrices = async () => {
    // Convert IST string to UTC Date
    function convertISTtoUTC(istTimeString) {
      const localIST = new Date(
        new Date(istTimeString).toLocaleString("en-US", {
          timeZone: "Asia/Kolkata",
        })
      );
      return new Date(localIST.toISOString());
    }

    if (!futureStartDate || !futureEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (new Date(futureEndDate) <= new Date(futureStartDate)) {
      toast.error("End date must be after start date");
      return;
    }

    const updates = Array.from(selectedProducts).map((id) => ({
      productId: id,
      newMrp: editValues[id]?.mrpPrice,
      newSel: editValues[id]?.selPrice,
      startTime: convertISTtoUTC(futureStartDate),
      endTime: convertISTtoUTC(futureEndDate),
    }));

    try {
      const response = await axios.post(
        "https://api.fitoja.in/api/schedule-product-sale",
        { products: updates }
      );

      if (response.data.success) {
        toast.success("Products scheduled successfully!");
        setSelectedProducts(new Set());
        setEditValues({});
        setFutureStartDate("");
        setFutureEndDate("");
      } else {
        toast.error(response.data.msg || "Update failed. Try again.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }
  };

  return (
    <div className="festival-sales-page">
      <h2 style={{ marginTop: "100px" }}>Festival Sales Product Selection</h2>

      <div style={{ margin: "20px 0" }}>
        <label>Sale Start Date:&nbsp;</label>
        <input
          type="datetime-local"
          value={futureStartDate}
          onChange={(e) => setFutureStartDate(e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
        />
      </div>

      <div style={{ margin: "20px 0" }}>
        <label>Sale End Date:&nbsp;</label>
        <input
          type="datetime-local"
          value={futureEndDate}
          onChange={(e) => setFutureEndDate(e.target.value)}
          min={futureStartDate || new Date().toISOString().slice(0, 16)}
        />
      </div>

      {loading ? (
        <div className="loading-indicator">
          <p>Loading products...</p>
        </div>
      ) : products.length > 0 ? (
        <div className="products-list">
          {products.map((product) => (
            <div key={product._id} className="products-card">
              <input
                type="checkbox"
                checked={selectedProducts.has(product._id)}
                onChange={() => handleCheckboxChange(product._id)}
              />
              <img src={product.productImage} alt={product.productName} />
              <div className="product-info">
                <p>
                  <strong>{product.productName}</strong>
                </p>
                <p>Current MRP: ₹{product.mrpPrice}</p>
                <p>Current Selling Price: ₹{product.selPrice}</p>
              </div>
              {selectedProducts.has(product._id) && (
                <div className="edit-inputs">
                  <input
                    type="number"
                    placeholder="New MRP"
                    value={editValues[product._id]?.mrpPrice || ""}
                    onChange={(e) =>
                      handleEditChange(product._id, "mrpPrice", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    placeholder="New Selling Price"
                    value={editValues[product._id]?.selPrice || ""}
                    onChange={(e) =>
                      handleEditChange(product._id, "selPrice", e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p>No products available.</p>
      )}

      {selectedProducts.size > 0 && (
        <div style={{ marginTop: "20px" }}>
          <button onClick={handleUpdatePrices} className="update-button">
            Schedule Selected Products
          </button>
        </div>
      )}
    </div>
  );
};

export default FestivalSalesProducts;
